import React from 'react';
import PropTypes from 'prop-types';
import { Form } from '@openedx/paragon';
import TextareaAutosize from 'react-textarea-autosize';

import SectionSubHeader from '../../generic/section-sub-header';
import DynamicStringList from '../../generic/dynamic-string-list';
import TagsChipsField from '../TagsChipsField';
import MetadataCopySection from './MetadataCopySection';

const CourseMetadataSection = ({
  courseId,
  courseSlug,
  subject,
  tags,
  topics,
  altText,
  level,
  levelOptions,
  promotion,
  trackName,
  learningCategory,
  learningSequence,
  accessDuration,
  jobReadySkills,
  certCount,
  skillOverview,
  skillSubject,
  learningList,
  outcomesList,
  requiredMaterialsDesc,
  requiredMaterialsList,
  credlyBadgeTemplateId,
  courseIdTrackArray,
  bongoLinkList,
  onChange,
}) => (
  <section className="section-container course-metadata-section">
    <SectionSubHeader
      title="Course Metadata"
      description="Extended metadata fields for this course."
    />

    <Form.Group className="form-group-custom">
      <Form.Label>Course Slug</Form.Label>
      <Form.Control
        value={courseSlug}
        onChange={(e) => onChange(e.target.value, 'courseSlug')}
      />
      <Form.Control.Feedback>
        A unique URL-friendly identifier for this course (e.g. intro-to-python).
      </Form.Control.Feedback>
    </Form.Group>

    <Form.Group className="form-group-custom">
      <Form.Label>Course Subject</Form.Label>
      <Form.Control
        value={subject}
        onChange={(e) => onChange(e.target.value, 'subject')}
      />
      <Form.Control.Feedback>
        The primary subject or discipline of this course.
      </Form.Control.Feedback>
    </Form.Group>

    <TagsChipsField
      fieldKey="tags"
      label="Course Tags"
      tags={tags || []}
      onChange={onChange}
    />

    <TagsChipsField
      fieldKey="topics"
      label="Course Topics"
      tags={topics || []}
      onChange={onChange}
    />

    <Form.Group className="form-group-custom">
      <Form.Label>Course Image Alt Text</Form.Label>
      <Form.Control
        value={altText}
        onChange={(e) => onChange(e.target.value, 'altText')}
      />
      <Form.Control.Feedback>
        Descriptive alt text for the course card image, used for accessibility.
      </Form.Control.Feedback>
    </Form.Group>

    {levelOptions.length > 0 && (
      <Form.Group className="form-group-custom">
        <Form.Label>Course Level</Form.Label>
        <Form.Control
          as="select"
          value={level}
          onChange={(e) => onChange(e.target.value, 'level')}
        >
          {levelOptions.map(({ value, label }) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </Form.Control>
        <Form.Control.Feedback>
          The difficulty or experience level required for this course.
        </Form.Control.Feedback>
      </Form.Group>
    )}

    <TagsChipsField
      fieldKey="promotion"
      label="Promotion Tags"
      tags={promotion || []}
      onChange={onChange}
    />

    <Form.Group className="form-group-custom">
      <Form.Label>Track Name</Form.Label>
      <Form.Control
        value={trackName}
        onChange={(e) => onChange(e.target.value, 'trackName')}
      />
      <Form.Control.Feedback>
        The name of the track this course belongs to.
      </Form.Control.Feedback>
    </Form.Group>

    <Form.Group className="form-group-custom">
      <Form.Label>Learning Category</Form.Label>
      <Form.Control
        value={learningCategory}
        onChange={(e) => onChange(e.target.value, 'learningCategory')}
      />
      <Form.Control.Feedback>
        The learning category used to classify this course in the catalog.
      </Form.Control.Feedback>
    </Form.Group>

    <Form.Group className="form-group-custom">
      <Form.Label>Learning Sequence</Form.Label>
      <Form.Control
        value={learningSequence}
        onChange={(e) => onChange(e.target.value, 'learningSequence')}
      />
      <Form.Control.Feedback>
        The position or order of this course within its learning sequence.
      </Form.Control.Feedback>
    </Form.Group>

    <Form.Group className="form-group-custom">
      <Form.Label>Duration</Form.Label>
      <Form.Control
        value={accessDuration}
        onChange={(e) => onChange(e.target.value, 'accessDuration')}
      />
      <Form.Control.Feedback>
        The access duration for this course (e.g. 30 days, 6 months).
      </Form.Control.Feedback>
    </Form.Group>

    <Form.Group className="form-group-custom">
      <Form.Label>Number of Job Ready Skills</Form.Label>
      <Form.Control
        type="number"
        min={0}
        value={jobReadySkills}
        onChange={(e) => onChange(e.target.value, 'jobReadySkills')}
      />
      <Form.Control.Feedback>
        The number of job-ready skills covered in this course.
      </Form.Control.Feedback>
    </Form.Group>

    <Form.Group className="form-group-custom">
      <Form.Label>Prepare for x Number of Certs</Form.Label>
      <Form.Control
        type="number"
        min={0}
        value={certCount}
        onChange={(e) => onChange(e.target.value, 'certCount')}
      />
      <Form.Control.Feedback>
        The number of certifications this course prepares learners for.
      </Form.Control.Feedback>
    </Form.Group>

    <Form.Group className="form-group-custom">
      <Form.Label>Overview for Skill</Form.Label>
      <Form.Control
        value={skillOverview}
        onChange={(e) => onChange(e.target.value, 'skillOverview')}
      />
      <Form.Control.Feedback>
        A brief overview of the skill focus for this course.
      </Form.Control.Feedback>
    </Form.Group>

    <Form.Group className="form-group-custom">
      <Form.Label>Subject for Skill</Form.Label>
      <Form.Control
        as={TextareaAutosize}
        value={skillSubject}
        onChange={(e) => onChange(e.target.value, 'skillSubject')}
      />
      <Form.Control.Feedback>
        The specific skill subject area covered in this course.
      </Form.Control.Feedback>
    </Form.Group>

    <Form.Group className="form-group-custom">
      <Form.Label>Learning List</Form.Label>
      <DynamicStringList
        items={learningList || []}
        fieldKey="learningList"
        label="Item"
        addLabel="Add Item"
        deleteLabel="Delete"
        placeholder="Enter learning item"
        onChange={onChange}
      />
      <Form.Control.Feedback>
        Key learning outcomes or topics covered in this course.
      </Form.Control.Feedback>
    </Form.Group>

    <Form.Group className="form-group-custom">
      <Form.Label>Topics List</Form.Label>
      <DynamicStringList
        items={outcomesList || []}
        fieldKey="outcomesList"
        label="Item"
        addLabel="Add Item"
        deleteLabel="Delete"
        placeholder="Enter topic"
        onChange={onChange}
      />
      <Form.Control.Feedback>
        The list of topics or outcomes covered in this course.
      </Form.Control.Feedback>
    </Form.Group>

    <Form.Group className="form-group-custom">
      <Form.Label>Required Materials Description</Form.Label>
      <Form.Control
        as={TextareaAutosize}
        value={requiredMaterialsDesc}
        onChange={(e) => onChange(e.target.value, 'requiredMaterialsDesc')}
      />
      <Form.Control.Feedback>
        A description of any materials learners must have before starting.
      </Form.Control.Feedback>
    </Form.Group>

    <Form.Group className="form-group-custom">
      <Form.Label>Required Materials List</Form.Label>
      <DynamicStringList
        items={requiredMaterialsList || []}
        fieldKey="requiredMaterialsList"
        label="Item"
        addLabel="Add Item"
        deleteLabel="Delete"
        placeholder="Enter required material"
        onChange={onChange}
      />
      <Form.Control.Feedback>
        Individual items learners are required to have or complete before starting.
      </Form.Control.Feedback>
    </Form.Group>

    <Form.Group className="form-group-custom">
      <Form.Label>Credly Badge Template ID</Form.Label>
      <Form.Control
        value={credlyBadgeTemplateId}
        onChange={(e) => onChange(e.target.value, 'credlyBadgeTemplateId')}
      />
      <Form.Control.Feedback>
        The Credly badge template ID to award upon course completion.
      </Form.Control.Feedback>
    </Form.Group>

    <TagsChipsField
      fieldKey="courseIdTrackArray"
      label="Array of Course IDs for a Track"
      tags={courseIdTrackArray || []}
      onChange={onChange}
    />

    <Form.Group className="form-group-custom">
      <Form.Label>Bongo Link List</Form.Label>
      <DynamicStringList
        items={bongoLinkList || []}
        fieldKey="bongoLinkList"
        label="Link"
        addLabel="Add Link"
        deleteLabel="Delete"
        placeholder="Enter Bongo link"
        onChange={onChange}
      />
      <Form.Control.Feedback>
        Links to associated Bongo video assignments for this course.
      </Form.Control.Feedback>
    </Form.Group>

    <MetadataCopySection courseId={courseId} />
  </section>
);

CourseMetadataSection.defaultProps = {
  courseSlug: '',
  subject: '',
  tags: [],
  topics: [],
  altText: '',
  level: '',
  levelOptions: [],
  promotion: [],
  trackName: '',
  learningCategory: '',
  learningSequence: '',
  accessDuration: '',
  jobReadySkills: '',
  certCount: '',
  skillOverview: '',
  skillSubject: '',
  learningList: [],
  outcomesList: [],
  requiredMaterialsDesc: '',
  requiredMaterialsList: [],
  credlyBadgeTemplateId: '',
  courseIdTrackArray: [],
  bongoLinkList: [],
};

CourseMetadataSection.propTypes = {
  courseId: PropTypes.string.isRequired,
  courseSlug: PropTypes.string,
  subject: PropTypes.string,
  tags: PropTypes.arrayOf(PropTypes.string),
  topics: PropTypes.arrayOf(PropTypes.string),
  altText: PropTypes.string,
  level: PropTypes.string,
  levelOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  promotion: PropTypes.arrayOf(PropTypes.string),
  trackName: PropTypes.string,
  learningCategory: PropTypes.string,
  learningSequence: PropTypes.string,
  accessDuration: PropTypes.string,
  jobReadySkills: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  certCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  skillOverview: PropTypes.string,
  skillSubject: PropTypes.string,
  learningList: PropTypes.arrayOf(PropTypes.string),
  outcomesList: PropTypes.arrayOf(PropTypes.string),
  requiredMaterialsDesc: PropTypes.string,
  requiredMaterialsList: PropTypes.arrayOf(PropTypes.string),
  credlyBadgeTemplateId: PropTypes.string,
  courseIdTrackArray: PropTypes.arrayOf(PropTypes.string),
  bongoLinkList: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
};

export default CourseMetadataSection;
